import { Suspense, lazy } from 'react';
import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from 'react-router';
import { RequireAuth } from '../lib/session';
import { LoginPage } from '../modules/auth/LoginPage';
import { ForgotPasswordPage, ResetPasswordPage } from '../modules/auth/PasswordPages';
import { TwoFactorPage } from '../modules/auth/TwoFactorPage';
import { TwoFactorSetupPage } from '../modules/auth/TwoFactorSetupPage';
import { BookingPage } from '../modules/booking/public/BookingPage';
import { ManagePage } from '../modules/booking/public/ManagePage';
import { CalendarModule } from '../modules/calendar/CalendarModule';
import { DialerModule } from '../modules/dialer/DialerModule';
import { ContactRecordPage } from '../modules/contacts/ContactRecordPage';
import { ContactsPage } from '../modules/contacts/ContactsPage';
import { DealsPage } from '../modules/deals/DealsPage';
import { DealRecordPage } from '../modules/deals/record/DealRecordPage';
import { CancellationSettingsPage } from '../modules/deals/settings/CancellationSettingsPage';
import { CommissionSettingsPage } from '../modules/deals/settings/CommissionSettingsPage';
import { FiguresPage } from '../modules/controlling/figures/FiguresPage';
import { SalesPage } from '../modules/controlling/sales/SalesPage';
import { FinancingsPage } from '../modules/financings/FinancingsPage';
import { FinancingRecordPage } from '../modules/financings/record/FinancingRecordPage';
import { FinancingCommissionSettingsPage } from '../modules/settings/FinancingCommissionSettingsPage';
import { CalculationAssumptionsSettingsPage } from '../modules/settings/CalculationAssumptionsSettingsPage';
import { MergePage } from '../modules/contacts/MergePage';
import { AvailabilitySettingsPage } from '../modules/settings/AvailabilitySettingsPage';
import { ConnectionsSettingsPage } from '../modules/settings/ConnectionsSettingsPage';
import { NotificationsSettingsPage } from '../modules/settings/NotificationsSettingsPage';
import { SecuritySettingsPage } from '../modules/settings/SecuritySettingsPage';
import { SettingsEmptyPage, SettingsFrame } from '../modules/settings/SettingsFrame';
import { WhatsAppSettingsPage } from '../modules/settings/WhatsAppSettingsPage';
import { BillingPage } from '../modules/settings/billing/BillingPage';
import { OrganizationBillingPage } from '../modules/settings/organization/billing/OrganizationBillingPage';
import { ModulesPage } from '../modules/settings/organization/modules/ModulesPage';
import { OrganizationTreePage } from '../modules/settings/organization/tree/OrganizationTreePage';
import { PkvPage } from '../modules/pkv/PkvPage';
import { PermissionsPage } from '../modules/settings/organization/permissions/PermissionsPage';
import { UserPermissionsPage } from '../modules/settings/organization/permissions/UserPermissionsPage';
import { PipelinesPage } from '../modules/settings/organization/pipelines/PipelinesPage';
import { IntegrationRunPage } from '../modules/settings/organization/integrations/IntegrationRunPage';
import { IntegrationsPage } from '../modules/settings/organization/integrations/IntegrationsPage';
import { TelephonyPage } from '../modules/settings/organization/telephony/TelephonyPage';
import { UsersPage } from '../modules/settings/organization/users/UsersPage';
import { AppointmentDetailPage } from '../modules/appointments/AppointmentDetailPage';
import { GuestPage } from '../modules/meetings/guest/GuestPage';
import { ReviewPage } from '../modules/meetings/review/ReviewPage';
import { RoomPage } from '../modules/meetings/room/RoomPage';
import { ApprovalsPage } from '../modules/approvals/ApprovalsPage';
import { QueueSessionPage } from '../modules/queue/QueueSessionPage';
import { DeveloperUploadPage } from '../modules/properties/public/DeveloperUploadPage';
import { portalRoutes } from '../modules/portal/routes';
import { propertiesRoutes } from '../modules/properties/routes';
import { ticketsRoutes } from '../modules/tickets/routes';
import { QueueStartPage } from '../modules/queue/QueueStartPage';
import { ReservationPublicPage } from '../modules/reservations/public/ReservationPublicPage';
import { SelfDisclosurePage } from '../modules/self-disclosure/SelfDisclosurePage';
import { SelfDisclosurePublicPage } from '../modules/self-disclosure/public/SelfDisclosurePublicPage';
import { ReferrerIntakePage } from '../modules/self-disclosure/referrer/ReferrerIntakePage';
import { ReferrerPartnersPage } from '../modules/settings/organization/referrers/ReferrerPartnersPage';
import { TasksPage } from '../modules/tasks/TasksPage';
import { HomePage } from '../modules/home/HomePage';
import { ChecklistPage } from '../modules/onboarding/ChecklistPage';
import { AdminWizard, AdminWizardIndex } from '../modules/onboarding/admin/AdminWizard';
import { HostGate, HostRoot } from '../modules/domain/HostRoot';
import { LegalPage } from '../modules/legal/LegalPage';
import { BrandSettingsPage } from '../modules/settings/organization/brand/BrandSettingsPage';
import { DomainSettingsPage } from '../modules/settings/organization/domain/DomainSettingsPage';
import { LegalSettingsPage } from '../modules/settings/organization/legal/LegalSettingsPage';
import { RequireSetup } from '../modules/onboarding/ui';
import { DonePage } from '../modules/onboarding/user/DonePage';
import { UserWizard } from '../modules/onboarding/user/UserWizard';
import { WelcomeRoute } from '../modules/onboarding/WelcomeRoute';
import { ProfileSettingsPage } from '../modules/settings/profile/ProfileSettingsPage';
import { AppShell, type RouteHandle } from '../shell/AppShell';
import { LockedRoute } from '../shell/ModulePages';
import { MorePage } from '../shell/MorePage';
import { ShellSkeleton } from '../shell/ShellSkeleton';

const settingsHandle = (title: string): RouteHandle => ({ mobileTitle: title, mobileBack: '/settings' });

/** The component check page is not part of the app bundle. */
const DsPage = lazy(() => import('../modules/ds/DsPage').then((module) => ({ default: module.DsPage })));

/** Routes of PLAN.md §8.3. Modules that are built in later milestones render their designed empty state. */
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<HostGate />}>
      <Route index element={<HostRoot />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/2fa" element={<TwoFactorPage />} />
      <Route path="/login/2fa/setup" element={<TwoFactorSetupPage />} />
      <Route path="/welcome/:token" element={<WelcomeRoute />} />
      <Route element={<RequireSetup fallback={<ShellSkeleton />} kind="user" />}>
        <Route path="/setup/user/done" element={<DonePage />} />
        <Route path="/setup/user/:step?" element={<UserWizard />} />
      </Route>
      <Route
        path="/setup/organization"
        element={<RequireSetup fallback={<ShellSkeleton />} kind="organization" />}
      >
        <Route index element={<AdminWizardIndex />} />
        <Route path=":step" element={<AdminWizard />} />
      </Route>
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset/:token" element={<ResetPasswordPage />} />
      <Route path="/r/:token" element={<ReservationPublicPage />} />
      <Route path="/sd/:token" element={<SelfDisclosurePublicPage />} />
      <Route path="/t/:token" element={<ReferrerIntakePage />} />
      {portalRoutes}
      <Route path="/u/:token" element={<DeveloperUploadPage />} />
      <Route path="/book/t/:token" element={<ManagePage />} />
      <Route path="/book/:orgSlug/:typeSlug" element={<BookingPage />} />
      <Route path="/m/:token" element={<GuestPage />} />
      <Route path="/legal/:orgSlug/:type" element={<LegalPage />} />
      <Route
        path="/ds"
        element={
          <Suspense fallback={<ShellSkeleton />}>
            <DsPage />
          </Suspense>
        }
      />

      <Route element={<RequireAuth fallback={<ShellSkeleton />} />}>
        <Route path="/calendar/room/:meetingId" element={<RoomPage />} />
        <Route element={<AppShell />}>
          <Route
            path="/home"
            element={<HomePage />}
            handle={{ mobileTitle: 'Start' } satisfies RouteHandle}
          />
          <Route path="/tasks" element={<TasksPage />} />
          <Route path="/tasks/new" element={<TasksPage create />} />
          <Route
            path="/setup/checklist"
            element={<ChecklistPage />}
            handle={{ mobileTitle: 'Checkliste', mobileBack: '/tasks' } satisfies RouteHandle}
          />
          <Route
            path="/tasks/approvals"
            element={<ApprovalsPage />}
            handle={{ mobileTitle: 'Freigaben', mobileBack: '/tasks' } satisfies RouteHandle}
          />
          <Route
            path="/tasks/queue"
            element={<QueueStartPage />}
            handle={{ mobileTitle: 'Queue starten', mobileBack: '/tasks' } satisfies RouteHandle}
          />
          <Route
            path="/tasks/queue/session"
            element={<QueueSessionPage />}
            handle={{ mobileTitle: 'Queue', mobileBack: '/tasks' } satisfies RouteHandle}
          />
          <Route
            path="/calendar/appointments/:id"
            element={<AppointmentDetailPage />}
            handle={{ mobileTitle: 'Termin', mobileBack: '/calendar' } satisfies RouteHandle}
          />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/new" element={<ContactsPage createOpen />} />
          <Route
            path="/contacts/:id"
            element={<ContactRecordPage />}
            handle={{ mobileTitle: 'Kontakt', mobileBack: '/contacts' } satisfies RouteHandle}
          />
          <Route
            path="/contacts/:id/self-disclosure/:selfDisclosureId"
            element={<SelfDisclosurePage />}
            handle={{ mobileTitle: 'Selbstauskunft', mobileBack: '/contacts' } satisfies RouteHandle}
          />
          <Route
            path="/tasks/duplicates/:candidateId"
            element={<MergePage />}
            handle={{ mobileTitle: 'Doppeleintrag', mobileBack: '/tasks/approvals' } satisfies RouteHandle}
          />
          <Route
            path="/calendar/room/:meetingId/review"
            element={<ReviewPage />}
            handle={{ mobileTitle: 'Rückblick', mobileBack: '/calendar' } satisfies RouteHandle}
          />
          <Route path="/calendar/*" element={<CalendarModule />} />
          {propertiesRoutes}
          <Route
            path="/controlling/sales"
            element={<SalesPage />}
            handle={{ mobileTitle: 'Vertrieb', mobileBack: '/controlling' } satisfies RouteHandle}
          />
          <Route
            path="/controlling/*"
            element={<FiguresPage />}
            handle={{ mobileTitle: 'Kennzahlen', mobileBack: '/tasks' } satisfies RouteHandle}
          />
          <Route path="/deals" element={<DealsPage />} />
          <Route path="/deals/new" element={<DealsPage createOpen />} />
          <Route
            path="/deals/:id"
            element={<DealRecordPage />}
            handle={{ mobileTitle: 'Deal', mobileBack: '/deals' } satisfies RouteHandle}
          />
          <Route path="/financing" element={<FinancingsPage />} />
          <Route path="/financing/new" element={<FinancingsPage createOpen />} />
          <Route
            path="/financing/:id"
            element={<FinancingRecordPage />}
            handle={{ mobileTitle: 'Finanzierung', mobileBack: '/financing' } satisfies RouteHandle}
          />
          <Route
            path="/dialer/*"
            element={<DialerModule />}
            handle={{ mobileTitle: 'Dialer' } satisfies RouteHandle}
          />
          {ticketsRoutes}
          <Route path="/pkv" element={<PkvPage />} handle={{ mobileTitle: 'PKV' } satisfies RouteHandle} />
          <Route path="/locked/:tool" element={<LockedRoute />} />
          <Route
            path="/more"
            element={<MorePage />}
            handle={{ mobileTitle: 'Mehr', mobileActions: false } satisfies RouteHandle}
          />

          <Route
            path="/settings"
            element={<SettingsFrame />}
            handle={{ mobileTitle: 'Einstellungen' } satisfies RouteHandle}
          >
            <Route
              path="notifications"
              element={<NotificationsSettingsPage />}
              handle={settingsHandle('Benachrichtigungen')}
            />
            <Route path="security" element={<SecuritySettingsPage />} handle={settingsHandle('Sicherheit')} />
            <Route path="profile" element={<ProfileSettingsPage />} handle={settingsHandle('Mein Profil')} />
            <Route
              path="connections"
              element={<ConnectionsSettingsPage />}
              handle={settingsHandle('Verbindungen')}
            />
            <Route
              path="whatsapp"
              element={<WhatsAppSettingsPage />}
              handle={{ mobileTitle: 'WhatsApp', mobileBack: '/settings/connections' } satisfies RouteHandle}
            />
            <Route
              path="availability"
              element={<AvailabilitySettingsPage />}
              handle={settingsHandle('Verfügbarkeit')}
            />
            <Route path="billing" element={<BillingPage />} handle={settingsHandle('Abrechnung')} />
            <Route
              path="organization/commission"
              element={<CommissionSettingsPage />}
              handle={settingsHandle('Provisionssätze')}
            />
            <Route
              path="organization/commission-financing"
              element={<FinancingCommissionSettingsPage />}
              handle={settingsHandle('Provision Finanzierungen')}
            />
            <Route
              path="organization/calculation-assumptions"
              element={<CalculationAssumptionsSettingsPage />}
              handle={settingsHandle('Rechenannahmen Selbstauskunft')}
            />
            <Route
              path="organization/deals"
              element={<CancellationSettingsPage />}
              handle={settingsHandle('Deals')}
            />
            <Route
              path="organization/users"
              element={<UsersPage />}
              handle={settingsHandle('Nutzer und Rollen')}
            />
            <Route
              path="organization/permissions"
              element={<PermissionsPage />}
              handle={settingsHandle('Rechte und Rollen')}
            />
            <Route
              path="organization/permissions/:userId"
              element={<UserPermissionsPage />}
              handle={settingsHandle('Rechte')}
            />
            <Route
              path="organization/pipelines"
              element={<PipelinesPage />}
              handle={settingsHandle('Pipelines und Lifecycle')}
            />
            <Route
              path="organization/referrers"
              element={<ReferrerPartnersPage />}
              handle={settingsHandle('Tippgeber')}
            />
            <Route path="organization/modules" element={<ModulesPage />} handle={settingsHandle('Module')} />
            <Route
              path="organization/tree"
              element={<OrganizationTreePage />}
              handle={settingsHandle('Organisationsbaum')}
            />
            <Route
              path="organization/telephony"
              element={<TelephonyPage />}
              handle={settingsHandle('Telefonie')}
            />
            <Route
              path="organization/billing"
              element={<OrganizationBillingPage />}
              handle={settingsHandle('Abrechnung der Organisation')}
            />
            <Route
              path="organization/brand"
              element={<BrandSettingsPage />}
              handle={settingsHandle('Marke')}
            />
            <Route
              path="organization/legal-texts"
              element={<LegalSettingsPage />}
              handle={settingsHandle('Rechtstexte')}
            />
            <Route
              path="organization/integrations"
              element={<IntegrationsPage />}
              handle={settingsHandle('Integrationen')}
            />
            <Route
              path="organization/integrations/runs/:runId"
              element={<IntegrationRunPage />}
              handle={
                {
                  mobileTitle: 'Lauf',
                  mobileBack: '/settings/organization/integrations',
                } satisfies RouteHandle
              }
            />
            <Route
              path="organization/domain"
              element={<DomainSettingsPage />}
              handle={settingsHandle('Domain und Subdomain')}
            />
            <Route
              path="organization/*"
              element={<SettingsEmptyPage />}
              handle={settingsHandle('Organisation')}
            />
          </Route>

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Route>
    </Route>,
  ),
);
